import { useState, useEffect, useMemo } from "react";
import { FaCheck, FaPrint, FaRegEdit, FaTimes, FaEye } from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link } from "react-router-dom";
import SmartDropdown from "../../Shared/SmartDropdown";
import DynamicSelect from "../../DynamicSelect";
import TableSkeleton from "../../TableSkeleton";
import { PrintLayout } from "../../PrintLayout";
import Pagination from "../../Shared/Pagination";

const AppointmentTable = ({
  data = [],
  onEdit,
  onDelete,
  currentPage = 1,
  itemsPerPage,
  onPageChange,
  formatDate,
  handleApproveClick,
  handleCancelClick,
  getStatusLabel,
  toggleDropdown,
  activeDropdown,
  dropdownTriggerRefs,
  handleView,
  setActiveDropdown,
  isLoading = false,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc"); 
  const [appointmentToPrint, setAppointmentToPrint] = useState(null);

  // Sorting options for DynamicSelect
  const sortOptions = [
    { value: "desc", label: "Newest to Oldest" },
    { value: "asc", label: "Oldest to Newest" },
  ];

  // Memoize sortedData to prevent new reference on every render
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [data, sortOrder]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const indexOfLastItem = validCurrentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    setSelectedRows(new Array(sortedData.length).fill(false));
  }, [sortedData]);

  useEffect(() => {
    // Sync currentPage with validCurrentPage if out of bounds
    if (currentPage !== validCurrentPage) {
      onPageChange(validCurrentPage);
    }
  }, [currentPage, validCurrentPage, onPageChange]);

  const handleRowCheckboxChange = (index) => {
    const actualIndex = indexOfFirstItem + index;
    const newSelectedRows = [...selectedRows];
    newSelectedRows[actualIndex] = !newSelectedRows[actualIndex];
    setSelectedRows(newSelectedRows);
  };

  const handleHeaderCheckboxChange = () => {
    const allChecked = currentData.every(
      (_, i) => selectedRows[indexOfFirstItem + i]
    );
    const newSelectedRows = [...selectedRows];
    currentData.forEach((_, i) => {
      newSelectedRows[indexOfFirstItem + i] = !allChecked;
    });
    setSelectedRows(newSelectedRows);
  };

  const selectedIds = currentData
    .filter((_, i) => selectedRows[indexOfFirstItem + i])
    .map((app) => app.id);

  const handleBulkDelete = () => {
    if (selectedIds.length) {
      onDelete(selectedIds);
    }
  };

  const handleSortChange = (selectedOption) => {
    setSortOrder(selectedOption.value);
    onPageChange(1);
  };

const initiatePrint = (appointment) => {
    setAppointmentToPrint(appointment);
    setTimeout(() => {
      handlePrintBill();
    }, 100);
  };

const handlePrintBill = () => {
    // This function is correct, no changes needed
    const elementsToHide = document.querySelectorAll(".print-hidden");
    elementsToHide.forEach((el) => (el.style.display = "none"));
    const printLayout = document.querySelector(".print-layout");
    if (printLayout) {
      printLayout.style.display = "block";
    }
    window.print();
    setTimeout(() => {
      elementsToHide.forEach((el) => (el.style.display = ""));
      if (printLayout) {
        printLayout.style.display = "none";
      }
      setAppointmentToPrint(null);
    }, 1000);
  };

  // Skeleton columns for loading state
  const skeletonColumns = [
    {
      width: "40px",
      height: "16px",
      className: "text-left p-3",
      skeletonClassName: "mx-3",
    }, // Checkbox
    {
      width: "60px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color p-3",
      skeletonClassName: "",
    }, // ID
    {
      width: "120px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color p-3",
      skeletonClassName: "",
    }, // Appointment Date
    {
      width: "150px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color p-3",
      skeletonClassName: "",
    }, // Patient
    {
      width: "150px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color p-3",
      skeletonClassName: "",
    }, // Doctor
    {
      width: "80px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color p-3",
      skeletonClassName: "",
    }, // Serial
    {
      width: "120px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color p-3",
      skeletonClassName: "",
    }, // Mobile
    {
      width: "100px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color p-3",
      skeletonClassName: "",
    }, // Status
    {
      width: "80px",
      height: "16px",
      className: "text-left p-3",
      skeletonClassName: "ml-auto",
    }, // Actions
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap border-b border-dashed border-M-text-color/50 p-4">
        <div className="inline-flex items-center gap-3 flex-1">
          <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Appointment List
          </h2>
          {selectedIds.length > 0 && (
            <button
              className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
              onClick={handleBulkDelete}
              disabled={isLoading}
              title="Delete selected rows"
            >
              <RiDeleteBin6Line />
            </button>
          )}
        </div>
        <div className="inline-flex flex-col-reverse flex-wrap sm:flex-row lg:items-center lg:justify-end gap-3 flex-1">
          <DynamicSelect
            options={sortOptions}
            onChange={handleSortChange}
            value={sortOptions.find((option) => option.value === sortOrder)}
            isSearchable={false}
            placeholder="Sort by Created Date"
            styles={{
              container: (provided) => ({
                ...provided,
                width: "200px",
              }),
            }}
          />
        <Link
          to="/add-appointment"
          className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
        >
          + Add Appointment
        </Link>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[1440px] w-full divide-y divide-gray-200">
          <thead>
            <tr className="px-3 bg-[#FBFBFD]">
              <th className="text-left">
                <div className="relative left-3 -top-[2px]">
                  <input
                    type="checkbox"
                    id="agreement"
                    className="hidden peer"
                    checked={currentData.every(
                      (_, i) => selectedRows[indexOfFirstItem + i]
                    )}
                    onChange={handleHeaderCheckboxChange}
                    disabled={isLoading}
                  />
                  <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                    <FaCheck className="text-slate-100" />
                  </span>
                  <label
                    htmlFor="agreement"
                    className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                  ></label>
                </div>
              </th>
              <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">ID</th>
              <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Appointment Date</th>
              <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Patient</th>
              <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Doctor</th>
              <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Serial</th>
              <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Mobile</th>
              <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Status</th>
              <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <TableSkeleton
                rowCount={itemsPerPage}
                columns={skeletonColumns}
                baseColor="#e5e7eb"
                highlightColor="#f3f4f6"
              />
            ) : currentData.length === 0 ? (
              <tr>
                <td colSpan={9} className="p-4 text-center">
                  No records found.
                </td>
              </tr>
            ) : (
              currentData.map((appointment, index) => {
                const actualIndex = indexOfFirstItem + index;
                return (
                  <tr
                    key={appointment.id}
                    className="border-b border-M-text-color/10 hover:bg-gray-100 border-collapse"
                  >
                    <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color">
                      <div className="relative left-3 -top-[2px]">
                        <input
                          type="checkbox"
                          id={"doc" + index}
                          className="hidden peer"
                          checked={selectedRows[actualIndex] || false}
                          onChange={() => handleRowCheckboxChange(index)}
                          disabled={isLoading}
                        />
                        <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                          <FaCheck className="text-slate-100" />
                        </span>
                        <label
                          htmlFor={"doc" + index}
                          className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                        ></label>
                      </div>
                    </td>
                    <td className="p-3 text-sm">{appointment.id}</td>
                    <td className="p-3 text-sm">
                      {formatDate(
                        appointment.appointmentDate || appointment.createdAt
                      )}
                    </td>
                    <td className="p-3 text-sm">
                      {appointment.patientName || "N/A"}
                    </td>
                    <td className="p-3 text-sm">
                      {appointment.doctor?.translations?.en?.name ||
                        appointment.doctor?.name ||
                        "Unknown"}
                    </td>
                    <td className="p-3 text-sm">
                      {appointment.serialNumber || "--"}
                    </td>
                    <td className="p-3 text-sm">
                      {appointment.mobileNumber || "N/A"}
                    </td>
                    <td className="p-3 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          appointment.status === "cancelled"
                            ? "bg-red-100 text-red-600"
                            : appointment.status === "approved"
                            ? "bg-green-100 text-green-700"
                            : "bg-orange-100 text-orange-600"
                        }`}
                      >
                        {getStatusLabel(appointment)}
                      </span>
                    </td>
                    <td className="p-3 relative">
                      <button
                        ref={(el) => (dropdownTriggerRefs.current[appointment.id] = el)}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdown(appointment.id);
                        }}
                        className="size-7 rounded-full bg-gray-100 text-gray-600 text-sm inline-flex items-center justify-center hover:bg-gray-200 transition-all duration-300"
                        disabled={isLoading}
                      >
                        <BsThreeDotsVertical />
                      </button>
                      <SmartDropdown
                        isOpen={activeDropdown === appointment.id}
                        triggerRef={{ current: dropdownTriggerRefs.current[appointment.id] }}
                        onClose={() => setActiveDropdown(null)}
                        options={{ width: 150 }}
                      >
                        <div>
                          <button
                            onClick={() => handleView(appointment)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            disabled={isLoading}
                          >
                            <FaEye className="text-blue-600" /> View
                          </button>
                          <button
                            onClick={() => onEdit(appointment)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            disabled={isLoading}
                          >
                            <FaRegEdit className="text-M-primary-color" /> Edit
                          </button>
                          <button
                            onClick={() => initiatePrint(appointment)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            disabled={isLoading}
                          >
                            <FaPrint className="text-gray-600" /> Print
                          </button>
                          {appointment.status === "pending" && (
                            <>
                              <button
                                onClick={() => handleApproveClick(appointment.id)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                disabled={isLoading}
                              >
                                <FaCheck className="text-green-600" /> Approve
                              </button>
                              <button
                                onClick={() => handleCancelClick(appointment.id)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                disabled={isLoading}
                              >
                                <FaTimes className="text-orange-600" /> Cancel
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => onDelete([appointment.id])}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            disabled={isLoading}
                          >
                            <RiDeleteBin6Line className="text-red-600" /> Delete
                          </button>
                        </div>
                      </SmartDropdown>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <Pagination
        currentPage={validCurrentPage}
        totalPages={totalPages}
        totalRecords={sortedData.length}
        itemsPerPage={itemsPerPage}
        onPageChange={onPageChange}
      />
      {appointmentToPrint && (
        <PrintLayout
          className="print-layout"
          title="Appointment Slip"
          billInfo={(
            <>
              <div>
                <p><strong>Appointment ID:</strong> {appointmentToPrint.id}</p>
                <p><strong>Date:</strong> {formatDate(appointmentToPrint.appointmentDate || appointmentToPrint.createdAt)}</p>
                <p><strong>Patient Name:</strong> {appointmentToPrint.patientName}</p>
                <p><strong>Mobile:</strong> {appointmentToPrint.mobileNumber || "N/A"}</p>
              </div>
              <div>
                <p><strong>Doctor:</strong> {appointmentToPrint.doctorName || "Unknown"}</p>
                <p><strong>Serial No:</strong> {appointmentToPrint.serialNumber || "--"}</p>
                <p><strong>Age:</strong> {appointmentToPrint.age || "N/A"}</p>
                <p><strong>Weight:</strong> {appointmentToPrint.weight || "N/A"}</p>
              </div>
            </>
          )}
          summary={(
          <>
            <div className="flex justify-between">
              <span>Consultation Fee:</span>
              <span>{appointmentToPrint.consultationFee || "N/A"} Tk</span>
            </div>
            <div className="flex justify-between">
              <span>Payment Method:</span>
              <span>{appointmentToPrint.paymentMethod || "N/A"}</span>
            </div>
          </>
          )}
          note={(
            <div className="note-box">
              <p className="font-bold">Important Instructions:</p>
              <ul className="list-disc pl-5">
                <li>Please arrive 15 minutes before your appointment time.</li>
                <li>For any changes, please contact us 24 hours in advance.</li>
                {appointmentToPrint.reason && (
                  <li><strong>Reason for Visit:</strong> {appointmentToPrint.reason}</li>
                )}
              </ul>
            </div>
          )}
          table={null}
          footer={null}
        />
      )}
    </div>
  );
};

export default AppointmentTable;